import { useQuery } from "@tanstack/react-query";
import { diagnosticsApi, type DxCategoryDto } from "@/api/diagnostics";
import { MasterListPage } from "./MasterListPage";
import { MasterFormPage } from "./MasterFormPage";
import { statusColumn, TextFieldCell, SelectCell, SwitchCell } from "./fields";

const BASE = "/diagnostics/categories";
const LIST_KEY = ["dx-categories"];

/** Departments as select options — the lookup both screens need. */
function useDepartmentOptions() {
  const q = useQuery({ queryKey: ["dx-departments"], queryFn: () => diagnosticsApi.listDepartments() });
  return {
    options: (q.data ?? []).map(d => ({ value: d.id, label: `${d.code} — ${d.name}` })),
    nameById: new Map((q.data ?? []).map(d => [d.id, `${d.code} — ${d.name}`])),
  };
}

export function DiagnosticsCategoriesPage() {
  const { nameById } = useDepartmentOptions();
  return (
    <MasterListPage<DxCategoryDto>
      breadcrumbs={["Diagnostics", "Masters"]}
      title="Test categories"
      subtitle="Catalog groupings under a department."
      queryKey={LIST_KEY}
      listFn={(inc) => diagnosticsApi.listCategories(undefined, inc)}
      deleteFn={diagnosticsApi.deleteCategory}
      basePath={BASE}
      newLabel="New category"
      labelOf={(r) => r.name}
      deleteNote="It will be refused while any lab test still belongs to it."
      searchPlaceholder="Search by code, name, or department"
      searchText={(r) => `${r.code} ${r.name} ${nameById.get(r.departmentId) ?? ""}`}
      columns={[
        { field: "code", headerName: "Code", width: 140 },
        { field: "name", headerName: "Name", flex: 1, minWidth: 220 },
        {
          field: "departmentId", headerName: "Department", width: 220,
          renderCell: (p) => nameById.get(p.row.departmentId) ?? "—",
        },
        statusColumn<DxCategoryDto>(),
      ]}
    />
  );
}

interface Values { departmentId: string; code: string; name: string; isActive: boolean }

export function CategoryFormPage() {
  const { options } = useDepartmentOptions();
  return (
    <MasterFormPage<Values, DxCategoryDto>
      breadcrumbs={["Diagnostics", "Test categories"]}
      noun="test category"
      basePath={BASE}
      listQueryKey={LIST_KEY}
      detailQueryKey={(id) => ["dx-category", id]}
      defaultValues={{ departmentId: "", code: "", name: "", isActive: true }}
      getFn={diagnosticsApi.getCategory}
      toValues={(d) => ({ departmentId: d.departmentId, code: d.code, name: d.name, isActive: d.isActive })}
      createFn={(v) => diagnosticsApi.createCategory({
        departmentId: v.departmentId, code: v.code.trim(), name: v.name.trim(),
      })}
      updateFn={(id, v) => diagnosticsApi.updateCategory(id, {
        departmentId: v.departmentId, code: v.code.trim(), name: v.name.trim(), isActive: v.isActive,
      })}
    >
      {({ control }, isEdit) => (
        <>
          <SelectCell control={control} name="departmentId" label="Department" span={12}
            required options={options} emptyLabel="— select department —" />
          <TextFieldCell control={control} name="code" label="Code" span={4} required />
          <TextFieldCell control={control} name="name" label="Name" span={8} required />
          {isEdit && <SwitchCell control={control} name="isActive" label="Active" />}
        </>
      )}
    </MasterFormPage>
  );
}
